import React, { useMemo } from 'react';
import sanitizer from '@helpers/sanitizer';
import { usePreferences } from '@context/preferences.context';
import { Breadcrumb } from '@interfaces/models/breadcrumb';
import { getSeoBaseUrlFromLocale } from '@helpers/utils/seo-structured-data';
import { BreadcrumbList, WithContext } from 'schema-dts';

type BreadcrumbSchemaProps = {
  breadcrumbs: Breadcrumb[];
} & React.HTMLProps<HTMLDivElement>;

const buildItemUrl = (breadcrumbUrl: Breadcrumb['url'], origin: string): string | undefined => {
  const path =
    breadcrumbUrl.path && breadcrumbUrl.path !== 'null'
      ? breadcrumbUrl.path
      : breadcrumbUrl.url
        ? new URL(breadcrumbUrl.url).pathname
        : '';

  if (!path) {
    return undefined;
  }

  return origin ? `${origin}${path}` : path;
};

export const createBreadcrumbSchema = (breadcrumbs: Breadcrumb[], origin = ''): WithContext<BreadcrumbList> => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: breadcrumbs.map((breadcrumb: Breadcrumb, index: number) => {
    const item = buildItemUrl(breadcrumb.url, origin);

    return {
      '@type': 'ListItem',
      position: index + 1,
      name: breadcrumb.localizedName,
      ...(item && { item }),
    };
  }),
});

const BreadcrumbSchema: React.FC<BreadcrumbSchemaProps> = (props): React.JSX.Element => {
  const { breadcrumbs } = props;
  const { locale } = usePreferences();
  const origin = getSeoBaseUrlFromLocale(locale);

  const schema: WithContext<BreadcrumbList> = useMemo(
    () => createBreadcrumbSchema(breadcrumbs, origin),
    [breadcrumbs, origin],
  );

  return (
    <script
      data-cy="breadcrumbs-schema"
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: sanitizer(JSON.stringify(schema)),
      }}
    />
  );
};

export default BreadcrumbSchema;
